import { GetStaticProps } from "next";
import Image from "next/image";
import Link from "next/link";
import { NextSeo } from "next-seo";
import Contact from "../../components/Contact";
import sdk from "../../lib/sdk";
import { Locale, PostsQuery } from "../../interfaces";
import Layout from "../../components/Layout";
import { useTranslations } from "next-intl";

type Props = {
  posts: PostsQuery["posts"];
  preview?: boolean;
};

const Blog = ({ preview, posts }: Props) => {
  const t = useTranslations("Blog");

  return (
    <Layout>
      <NextSeo title={t("title")} description={t("description")} />
      <div className="flex justify-center flex-1">
        <div className="flex flex-col items-center flex-1 mb-10">
          <h1 className="mb-10 text-4xl font-bold font-title">{t("title")}</h1>
          {posts.map((post) => (
            <Link href={"/blog/" + post.slug} key={post.slug}>
              <div className="flex flex-wrap w-full h-full px-4 my-8 cursor-pointer md:overflow-hidden md:px-0 md:w-3/4 md:rounded-lg md:shadow-lg md:hover:shadow-2xl md:hover:bg-sky-50 md:bg-gray-200">
                <div className="relative w-full overflow-hidden aspect-video md:w-1/2">
                  <Image
                    src={post.coverImage?.url || "/logo-with-background.webp"}
                    layout="fill"
                    alt={post.title}
                    className="absolute object-contain w-full h-full md:object-cover"
                  />
                  {/* Trick for responsive image */}
                  <div className="block" style={{ paddingTop: "56.25%" }} />
                </div>
                <div className="flex flex-col justify-center my-4 space-y-2 md:px-8 md:w-1/2">
                  <div className="text-2xl font-bold font-title">
                    {post.title}
                  </div>
                  <div className="text-sm text-gray-400">
                    {new Date(post.publishedAt).toLocaleDateString() ||
                      post.createdAt}
                  </div>
                  <div>{post.excerpt}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Contact />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  preview = false,
}) => {
  const { posts } = await sdk.Posts({
    locale: locale as Locale,
    fallbackLocale: locales?.filter((l) => l !== locale)[0] as Locale,
  });

  return {
    props: {
      preview,
      posts: posts,
      messages: (await import(`../../messages/${locale}.json`)).default,
    },
  };
};

export default Blog;
